import React, { forwardRef, useRef, useState } from 'react';

import { Select } from '@aline/common';
import { link } from '@aline/routes';
import type { MenuProps } from '@mui/material';
import { ButtonBase, Menu, MenuItem, Typography } from '@mui/material';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { handleLogout } from 'src/auth';
import { AlineAvatar, useLeftNavItemStyles, UserProfileDialog } from 'src/components';
import { useSelectCurrentUser } from 'src/gql';
import { getEntityDisplayDetails } from 'src/helpers';
import { signatureDialogOpenVar } from 'src/layout-engine';
import type { UserType } from 'src/types';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  avatarRing: {
    border: '2px solid rgba(255, 255, 255, 0.8)',
    borderRadius: '50%',
    display: 'flex',
  },
  displayName: {
    color: theme.palette.common.white,
  },
  menu: {
    marginLeft: theme.spacing(1),
    minWidth: 225,
  },
  userDetailsContainer: {
    alignItems: 'center',
    borderBottom: `1px solid ${theme.appColors.rule}`,
    display: 'flex',
    marginBottom: theme.spacing(1),
    padding: theme.spacing(2),
  },
  userNamesContainer: {
    marginLeft: theme.spacing(2),
  },
}));

interface UserDetailsProps {
  currentUser: UserType;
}

const UserDetails = forwardRef(function UserDetails(
  { currentUser }: UserDetailsProps,
  ref: React.Ref<HTMLDivElement>
): JSX.Element | null {
  const { classes } = useStyles();
  const { avatarColor, primary, secondary } = getEntityDisplayDetails(currentUser);

  return (
    <div ref={ref} className={classes.userDetailsContainer}>
      <AlineAvatar avatarInfo={currentUser} styles={{ backgroundColor: avatarColor, size: 'md' }} />
      <div className={classes.userNamesContainer}>
        <Typography variant="body1">{primary}</Typography>
        {secondary && <Typography variant="caption">{secondary}</Typography>}
      </div>
    </div>
  );
});

export function ProfileIconMenu({
  className,
}: React.HTMLAttributes<HTMLElement>): JSX.Element | null {
  const currentUser = useSelectCurrentUser();
  const avatarRef = useRef<HTMLButtonElement | null>(null);
  const [profileOpen, setProfileOpen] = useState<boolean>(false);

  return (
    <>
      <ButtonBase
        ref={avatarRef}
        aria-label="Profile menu"
        className={className}
        data-cy={Select.topNav.profileMenuButton}
        onClick={() => setProfileOpen(!profileOpen)}
      >
        <AlineAvatar avatarInfo={currentUser} styles={{ size: 'md' }} />
      </ButtonBase>
      <ProfileMenu
        anchorEl={avatarRef.current}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        currentUser={currentUser}
        open={profileOpen}
        setOpen={setProfileOpen}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      />
    </>
  );
}

interface ProfileFlyoutMenuProps {
  className?: string;
}

export function ProfileFlyoutMenu({ className }: ProfileFlyoutMenuProps): JSX.Element | null {
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [profileOpen, setProfileOpen] = useState<boolean>(false);
  const { classes: navItemClasses } = useLeftNavItemStyles({ selected: false });

  return (
    <>
      <ButtonBase
        ref={buttonRef}
        aria-label="Profile menu"
        className={clsx(navItemClasses.content, className)}
        data-cy={Select.topNav.profileMenuButton}
        onClick={() => setProfileOpen(!profileOpen)}
      >
        <div className={classes.avatarRing}>
          <AlineAvatar avatarInfo={currentUser} />
        </div>
      </ButtonBase>
      <ProfileMenu
        anchorEl={buttonRef.current}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        currentUser={currentUser}
        open={profileOpen}
        setOpen={setProfileOpen}
        transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      />
    </>
  );
}

interface ProfileMenuProps {
  anchorEl: HTMLButtonElement | null;
  anchorOrigin: MenuProps['anchorOrigin'];
  currentUser: UserType;
  open: boolean;
  setOpen: (open: boolean) => void;
  transformOrigin: MenuProps['transformOrigin'];
}

function ProfileMenu({
  anchorEl,
  anchorOrigin,
  currentUser,
  open,
  setOpen,
  transformOrigin,
}: ProfileMenuProps): JSX.Element | null {
  const { classes } = useStyles();
  const [editProfileOpen, setEditProfileOpen] = useState<boolean>(false);

  async function logoutHandler(): Promise<void> {
    await handleLogout();
    setOpen(false);
  }

  if (!currentUser) return null;

  return (
    <>
      <Menu
        PopoverClasses={{ paper: classes.menu }}
        anchorEl={anchorEl}
        anchorOrigin={anchorOrigin}
        marginThreshold={0}
        open={open}
        transformOrigin={transformOrigin}
        onClose={() => setOpen(false)}
      >
        <UserDetails currentUser={currentUser} />
        <MenuItem
          component={Link}
          data-cy={Select.topNav.settingsMenuItem}
          to={link.AdminOrgIndex()}
          onClick={() => setOpen(false)}
        >
          Settings
        </MenuItem>
        <MenuItem onClick={logoutHandler}>Log out</MenuItem>
        <MenuItem
          onClick={() => {
            setOpen(false);
            setEditProfileOpen(true);
          }}
        >
          Edit profile
        </MenuItem>
        <MenuItem
          data-cy={Select.topNav.setSignaturesMenuItem}
          onClick={() => {
            setOpen(false);
            signatureDialogOpenVar(true);
          }}
        >
          Update Signature
        </MenuItem>
      </Menu>
      <UserProfileDialog open={editProfileOpen} setOpen={setEditProfileOpen} user={currentUser} />
    </>
  );
}
